import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import SeoHead from '@/components/SeoHead';

const principleCards = [
  {
    title: 'Our Vision',
    body: 'Our vision is to penetrate the market and be recognized as a prestigious shop through focused business development and differentiation through innovation.',
  },
  {
    title: 'Our Mission',
    body: 'To be the number one brand in the footwear market, exceeding our client expectations through identification and satisfaction of customer needs by providing stylish and high quality products in a wide range.',
  },
  {
    title: 'Our Promise',
    body: 'We are committed to building a trusted footwear brand that combines comfort, style, and dependable everyday value.',
  },
];

export default function AboutPage() {
  return (
    <div className="bg-[#fffdf7]">
      <SeoHead
        title="About Trendy Quest Collection | Footwear Store in Nairobi"
        description="Learn about Trendy Quest Collection, our mission, and our commitment to stylish, comfortable, and practical footwear for everyday wear in Kenya."
        path="/about"
      />
      <section className="px-4 pb-6 pt-4 sm:pt-6 md:px-6">
        <div className="relative overflow-hidden rounded-[1.35rem] border border-[#e8dcc3] shadow-[0_24px_80px_rgba(89,72,28,0.14)] sm:rounded-[2.8rem]">
          <div className="relative h-[240px] w-full sm:h-[300px] lg:h-[380px]">
            <Image
              src="/homepage banner.jpg"
              alt="Trendy Quest footwear banner"
              fill
              priority
              sizes="100vw"
              className="object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,250,235,0.82),rgba(255,247,214,0.32),rgba(18,18,18,0.14))]" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="max-w-3xl px-6 text-center sm:px-10 lg:px-14">
              <p className="mx-auto inline-flex rounded-2xl bg-green px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white shadow-[0_10px_30px_rgba(20,68,37,0.28)] sm:px-5 sm:py-3 sm:text-sm sm:tracking-[0.3em]">
                About Trendy Quest
              </p>
              <h1 className="mt-4 text-3xl font-playfair font-bold leading-[1.02] text-[#111111] sm:text-4xl lg:text-5xl">
                Quality footwear for confident everyday living
              </h1>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 py-10 md:px-6 lg:py-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid gap-10"
        >
          <div className="rounded-[1.35rem] border border-[#ebe3ce] bg-white/80 p-4 text-center shadow-[0_18px_50px_rgba(20,68,37,0.06)] backdrop-blur-sm sm:rounded-[2.2rem] sm:p-9">
            <p className="mx-auto inline-flex rounded-2xl bg-green px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white shadow-[0_10px_30px_rgba(20,68,37,0.2)] sm:px-5 sm:py-3 sm:text-sm sm:tracking-[0.24em]">
              Who We Are
            </p>
            <div className="mt-5 space-y-5 text-lg leading-8 text-[#47433d]">
              <p>
                At Trendy Quest Collection, we bring you quality footwear that blends style, comfort, and everyday practicality.
              </p>
              <p>
                Our mission is simple, help you step out confidently, every single day.
              </p>
              <p>
                We focus on footwear that feels good, looks polished, and fits naturally into real life, whether you are dressing for work,
                casual days, or your next outing.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link
                href="/products"
                className="inline-flex w-full items-center justify-center rounded-full bg-green px-5 py-3 text-base font-semibold text-white transition hover:bg-[#24552d] sm:w-auto sm:px-8 sm:py-4 sm:text-lg"
              >
                Shop Now
              </Link>
              <Link
                href="/contact"
                className="inline-flex w-full items-center justify-center rounded-full border-2 border-green px-5 py-3 text-base font-semibold text-green transition hover:bg-green hover:text-white sm:w-auto sm:px-8 sm:py-4 sm:text-lg"
              >
                Get in Touch
              </Link>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-3 lg:items-stretch">
            {principleCards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.08, duration: 0.45 }}
                className="relative h-full overflow-hidden rounded-[1.35rem] border border-[#d2b885] bg-[#f9f2e4] shadow-[0_26px_90px_rgba(70,55,25,0.14)] sm:rounded-[2.4rem]"
              >
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.7),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(215,180,105,0.18),transparent_35%)]" />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.86),rgba(248,239,222,0.95))]" />
                <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-gradient-to-b from-transparent via-[#c7a465] to-transparent opacity-40" />
                <div className="relative flex h-full flex-col px-4 py-5 text-center sm:px-8 sm:py-10">
                  <p className="mx-auto inline-flex rounded-2xl bg-[#2f6d39] px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white shadow-[0_10px_30px_rgba(20,68,37,0.2)] sm:px-5 sm:py-3 sm:text-sm sm:tracking-[0.3em]">
                    {card.title}
                  </p>
                  <p className="mx-auto mt-5 max-w-md text-lg font-semibold leading-8 text-[#1d1a14] sm:text-xl">
                    {card.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>
    </div>
  );
}
